// Fax Message Types
export type FaxDirection = 'Incoming' | 'Outgoing'

export type FaxStatus =
  | 'draft'
  | 'received'
  | 'queued'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'failed'

export type FaxSystemView =
  | 'inbox'
  | 'sent'
  | 'draft'
  | 'archive'
  | 'trash'
  | 'history'
  | 'blocked'

export type FaxIntent =
  | 'create_draft'
  | 'save_draft'
  | 'send'
  | 'receive'
  | 'mark_sent'
  | 'mark_delivered'
  | 'mark_failed'
  | 'archive'
  | 'unarchive'
  | 'move_to_trash'
  | 'restore'
  | 'move_to_folder'
  | 'patch'

export interface IFaxMessage {
  fax_message_id?: string
  clinic_id: string
  fax_coversheet_id?: string | null
  contact_id?: string | null
  direction: FaxDirection
  from_number: string
  to_number: string
  status?: FaxStatus
  external_id?: string | null
  page_count?: number | null
  subject?: string | null
  cover_note?: string | null
  cover_snapshot_html?: string | null
  cover_snapshot_text?: string | null
  coversheet_mode?: FaxCoversheetModeDb | null
  draft_compose_state?: string | null
  sent_by?: string | null
  contact?: IFaxContact | null
  is_hidden?: boolean
  is_read?: boolean
  is_archived?: boolean
  fax_tag_id?: string
  fax_folder_id?: string | null
  date_time_created_at?: Date | string
  date_time_updated_at?: Date | string
  deleted_at?: Date | string | null
  display_status?: FaxStatus
  history_eligible?: boolean
  system_view?: FaxSystemView | null
  intent?: FaxIntent

  coversheet?: IFaxCoversheet
  documents?: IFaxDocument[]
  fax_tag?: IFaxTag
  fax_folder?: IFaxFolder | null
}

export interface IFaxContact {
  contact_id?: string
  first_name?: string
  middle_name?: string | null
  last_name?: string
  cellphone?: string
  fax_number?: string | null
  email?: string
  photo?: string | null
}

// Fax Coversheet Types
export interface IFaxCoversheet {
  fax_coversheet_id?: string
  clinic_id: string
  file_name?: string | null
  content: string
  description?: string | null
  thumbnail_url?: string | null
  template_kind?: CoversheetTemplateKind
  template_key?: string | null
  sort_order?: number
  date_time_created_at?: Date | string
  date_time_updated_at?: Date | string
  deleted_at?: Date | string | null
}

// Fax Document Types
export interface IFaxDocument {
  fax_document_id?: string
  clinic_id: string
  fax_message_id: string
  file_url: string
  file_name?: string | null
  mime_type?: string | null
  file_size?: number | null
  sort_order: number
  date_time_created_at?: Date | string
  date_time_updated_at?: Date | string
  deleted_at?: Date | string | null
}

// Fax Tag Types
export enum FaxTagColor {
  Default = 'gray-400',
  Red = 'red-400',
  Orange = 'orange-300',
  Yellow = 'yellow-400',
  Green = 'vivid-green-400',
  Blue = 'blue-400',
  Indigo = 'indigo-400',
}

export interface IFaxTag {
  fax_tag_id?: string
  clinic_id: string
  tag_name: string
  color: FaxTagColor
  date_time_created_at?: Date | string
  date_time_updated_at?: Date | string
}

export interface IFaxFolder {
  fax_folder_id?: string
  clinic_id: string
  folder_name: string
  date_time_created_at?: Date | string
  date_time_updated_at?: Date | string
}

export type IFaxMessageDatagrid = IFaxMessage & {
  _number: string
  _date: string
}

// Fax coversheet types
// Custom field types are persisted as rows in `IFaxCoversheetContent.fields`.
export type CoversheetCustomFieldType = 'input' | 'date' | 'checklist'

// Auto field types are compose-time helpers that resolve into the reserved
// header slots (to / from / sentOn) and must NOT be persisted in `fields`.
export type CoversheetAutoFieldType =
  | 'recipient_name'
  | 'recipient_fax'
  | 'recipient_email'
  | 'sender_name'
  | 'sender_fax'
  | 'sender_email'
  | 'current_date'

export type CoversheetFieldType =
  | CoversheetCustomFieldType
  | CoversheetAutoFieldType

export type CoversheetFieldSource = 'custom' | CoversheetAutoFieldType

export type CoversheetChecklistItem = {
  id: string
  label?: string
  checked?: boolean
}

export type CoversheetField = {
  id: string
  type: CoversheetFieldType
  label?: string
  value?: string
  dateValue?: Date
  items?: CoversheetChecklistItem[]
}

export type CoversheetTemplateKind = 'system' | 'custom'

export type CoversheetMode = 'template' | 'uploaded' | 'skipped'

export type FaxCoversheetModeDb = 'skipped' | 'template' | 'uploaded'

// Which contact field a fax should be routed to.
export type RecipientDestination = 'fax_number' | 'email'

export type FaxPreviewContentKind = 'html' | 'no_coversheet' | 'not_available'

export type FaxPreviewContent = {
  kind: FaxPreviewContentKind
  html?: string
}

export interface IFaxDraftComposeState {
  version: 1
  step: number
  coversheetMode: CoversheetMode
  selectedTemplateId?: string
  coverContent?: IFaxCoversheetContent
  contactId?: string
  recipientDestination?: RecipientDestination
  pendingDocuments?: {
    file_name: string
    mime_type?: string
    file_size?: number
  }[]
}

export interface IFaxCoversheetContent {
  version: 1
  title?: string
  to?: {
    contactId?: string
    recipientName?: string
    recipientFax?: string
    recipientEmail?: string
    optionalNote?: string
  }
  from?: {
    clinicLineId?: string
    displayName: string
    faxNumber: string
    senderEmail?: string
  }
  sentOn?: string
  fields: CoversheetField[]
  messageHtml?: string
  messageText?: string
}

// Structured summary used by the detail preview meta grid.
export type FaxCoversheetMeta = {
  toLabel: string
  toFax: string
  toEmail?: string
  fromLabel: string
  fromFax: string
  subject: string
  sentOn: string
  coversheetType: 'Template' | 'Uploaded' | 'None'
  templateName?: string
  pageCount: number
  customFields: { label: string; value: string }[]
}

export interface IFaxComposeSeed {
  intent: 'reply' | 'forward'
  subject?: string
  contactId?: string | null
  coversheetMode?: CoversheetMode
  coverContent?: IFaxCoversheetContent
}
